import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import {getIconForLanguage, getNameForLanguage} from 'crqlar-react-common/dist/utils/translations.util';
import {useCallback, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';

/*
    The cells every guest table shares, so a stay and a language read the same wherever guests are listed: the
    automation step preview's people and the survey invite's guest list.
 */

const noonOf = (day: string) => new Date(`${day}T12:00:00`);

/** A stay as "13 Oct – 14 Oct" in the user's language; one end alone when only one is known, '' when neither. */
export function useStayDates() {
    const {i18n} = useTranslation();
    const language = i18n?.resolvedLanguage || i18n?.language;
    const days = useMemo(() => new Intl.DateTimeFormat(language, {day: 'numeric', month: 'short'}), [language]);

    // Stable per language, so a table's column definitions can depend on it.
    return useCallback((from?: string, to?: string): string => {
        if (from && to) return `${days.format(noonOf(from))} – ${days.format(noonOf(to))}`;
        if (from) return days.format(noonOf(from));
        return to ? days.format(noonOf(to)) : '';
    }, [days]);
}

/**
 * The guest's language as its flag, its name on hover; a dash when unknown. The flags come from an external host:
 * when one does not load, the name stands in, so the cell is never blank.
 */
export function GuestLanguageFlag({language}: {language?: string}) {
    const flag = getIconForLanguage(language);
    const name = language ? getNameForLanguage(language, true, true) || language : '';
    const [failed, setFailed] = useState<string>();

    if (!flag) return <Typography variant={'body1'} color={'text.secondary'}>—</Typography>;
    if (failed === flag) return <Typography variant={'body1'} noWrap>{name}</Typography>;

    return (
        <Tooltip title={name}>
            <Box
                component={'img'}
                src={flag}
                alt={name}
                onError={() => setFailed(flag)}
                sx={{display: 'block', width: 21, height: 14, borderRadius: '2px', objectFit: 'cover'}}
            />
        </Tooltip>
    );
}
